import { createStyles, withStyles } from '@mui/styles';
import React, { Component } from 'react';
import { GridSortModel, GridSortDirection, GridSortItem, GridColumnVisibilityModel } from '@mui/x-data-grid';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { Theme, Grid, Paper, Container } from '@mui/material';
import UrlConstants from '../../../constants/UrlConstants';
import { RootState } from '../../../setup';
import Pagination from '../../../interfaces/common/pagination';
import Paging from '../../../interfaces/common/paging';
import IMenuItem from '../../../interfaces/common/menuItem';
import MainLayout from '../../common/widgets/mainLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchFactorSetWeights, getFactorSetWeights, isLoadingFactorSetWeights } from '../../../reducers/factorSetWeightReducer';
import WeightFilters from './weightFilters';
import WeightList from './weightList';
import FactorSetsFiltersComponent from '../factorSetsFiltersComponent';
import FactorSetOut from '../../../interfaces/output/factorSetOut';
import LanguageUtils from '../../../utils/LanguageUtils';
import PageUtils from '../../../utils/pageUtils';
import QuickSearchToolbar from '../../common/quickSearchToolbar';

interface IFactorSetWeightsProps {
    factorSetWeights: Pagination<FactorSetOut>;
    classes: any;
    fetchFactorSetWeights: any;
    isLoading: boolean;
    onChange: () => void;
}

interface IFactorSetWeightsState {
    searchTerm: string;
    paging: Paging;
    menuItem: IMenuItem;
    sortModel: GridSortModel;
    filters: WeightFilters;
    columnVisibilityModel: GridColumnVisibilityModel;
}

const messages = {
    factorSetWeights: LanguageUtils.createMessage('Factor set weights'),
    add: LanguageUtils.createMessage('Add')
};

const styles = (theme: Theme) => createStyles({
    container: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2)
    },
    paper: {
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column'
    }
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    fetchFactorSetWeights: (paging: Paging, filters: WeightFilters) => dispatch(fetchFactorSetWeights({
        paging,
        filters
    }))
});

const mapStoreToProps = (store: RootState) => {
    return {
        factorSetWeights: getFactorSetWeights(store),
        isLoading: isLoadingFactorSetWeights(store)
    };
};

class FactorSetWeights extends Component<IFactorSetWeightsProps, IFactorSetWeightsState> {
    weightListRef: any;
    constructor(props: IFactorSetWeightsProps) {
        super(props);
        this.weightListRef = React.createRef();

        this.state = {
            searchTerm: '',
            paging: PageUtils.getDefaultPaging(),
            filters: {
                name: ''
                // default: true
            } as WeightFilters,
            menuItem: {
                id: 1,
                name: messages.factorSetWeights,
                icon: <FontAwesomeIcon icon="weight-hanging" size="1x" transform="grow-7" />,
                isSelected: false
            },
            sortModel: [{
                field: '',
                sort: '' as GridSortDirection
            } as GridSortItem],
            columnVisibilityModel: {}
        };
    }

    componentDidMount() {
        const { fetchFactorSetWeights } = this.props;
        const { paging, filters } = this.state;

        fetchFactorSetWeights(paging, filters);
    }

    onChange = (attribute: string, value: any) => {
        const { filters, paging } = this.state;

        const newFilter = { ...filters } as WeightFilters;
        (newFilter as any)[attribute] = value;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };

        this.setState({
            filters: newFilter,
            paging: newPagination
        });
    }

    applyFilters = () => {
        const { fetchFactorSetWeights } = this.props;
        const { paging, filters } = this.state;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };
        this.setState({
            paging: newPagination
        });

        fetchFactorSetWeights(newPagination, filters);

        if(this.weightListRef !== null && this.weightListRef !== undefined &&
            this.weightListRef.current !== null && this.weightListRef.current !== undefined) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.weightListRef.current.resetDataGridPage();
        }

    }

    setDefaultState = () => {
        const { fetchFactorSetWeights } = this.props;
        const { paging } = this.state;

        const newFilters = { name: '' } as WeightFilters;

        const newSortModel = [{
            field: '',
            sort: '' as GridSortDirection
        } as GridSortItem] as GridSortModel;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page,
            sort: newSortModel
        };

        this.setState({
            paging: newPagination,
            filters: newFilters,
            searchTerm: ''
        });

        fetchFactorSetWeights(newPagination, newFilters);

        if(this.weightListRef !== null && this.weightListRef !== undefined &&
            this.weightListRef.current !== null && this.weightListRef.current !== undefined) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.weightListRef.current.resetDataGridPage();
        }
    }

    onChangeQuickSearch = (value: any) => {
        const { filters,paging } = this.state;
        const { fetchFactorSetWeights } = this.props;

        const newFilter = { ...filters } as WeightFilters;
        (newFilter as any)['name'] = value;

        const newPaging = PageUtils.getDefaultPaging();
        const newPagination = {
            ...paging,
            page: newPaging.page
        };

        this.setState({
            filters: newFilter,
            paging: newPagination,
            searchTerm: value
        });

        fetchFactorSetWeights(newPagination, newFilter);
    }

    _onSortChange = (newModel: GridSortModel) => {
        const { fetchFactorSetWeights } = this.props;
        const { sortModel, filters, paging } = this.state;

        if(JSON.stringify(sortModel) !== JSON.stringify(newModel)) {
            const newPagination = {
                ...paging,
                sort: newModel
            };
            this.setState({
                sortModel: newModel,
                paging: newPagination
            });

            fetchFactorSetWeights(newPagination, filters);
        }
    }

    render() {
        const { classes, factorSetWeights, fetchFactorSetWeights, isLoading } = this.props;
        const { searchTerm, paging, filters, menuItem, columnVisibilityModel } = this.state;

        return (
            <MainLayout menuItem={menuItem}>
                <Container maxWidth="xl" className={classes.container}>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            <Paper className={classes.paper}>
                                <QuickSearchToolbar
                                    onSearch={this.onChangeQuickSearch}
                                    searchTerm={searchTerm}
                                    includeFilters={true}
                                    actions={[{
                                        text: messages.add,
                                        icon: 'plus',
                                        href: `${UrlConstants.FACTOR_SETS}/weight/add`
                                    }]}
                                >
                                    <FactorSetsFiltersComponent applyFilters={this.applyFilters} setDefaultState={this.setDefaultState} filters={filters}
                                        onChange={this.onChange} />
                                </QuickSearchToolbar>
                                <WeightList rows={factorSetWeights} isLoading={isLoading}
                                    ref={this.weightListRef}
                                    onPageChange={(nextPage: number, nextSize: number) => {
                                        const newPage = {
                                            ...paging,
                                            page: nextPage,
                                            size: nextSize
                                        };
                                        fetchFactorSetWeights(newPage, filters);
                                        this.setState({ paging: newPage });
                                    }}
                                    onSortModelChange={(sortModel: GridSortModel) => this._onSortChange(sortModel)}
                                    columnVisibilityModel={columnVisibilityModel}
                                    onColumnVisibilityModelChange={(newModel : GridColumnVisibilityModel) =>
                                        this.setState({
                                            columnVisibilityModel: newModel
                                        })
                                    }
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                </Container>
            </MainLayout>
        );
    }
}

export default withStyles(styles)(connect(mapStoreToProps, mapDispatchToProps)(FactorSetWeights));